// import './scss/index.scss';

import { useState } from 'react';

import { useIntl } from 'react-intl';
import { commonMessages } from '@temp/intl';

import { ProductVariantPicker } from '@components/organisms/ProductVariantPicker';
import { QuantityInput } from '@components/molecules/QuantityInput';
import { SaveForLaterButton } from '@components/molecules/SaveForLaterButton';
import { AddToCartButton } from '@components/molecules/AddToCartButton';
import { EstimatedShipping } from '@components/molecules/EstimatedShipping';

import {
  canAddToCart,
  canAddSampleToCart,
  getAvailableQuantity,
  getProductPrice,
  LOW_STOCK_QUANTITY,
  MIN_SAMPLE_ITEMS,
  MAX_SAMPLE_ITEMS,
} from './stockHelpers';

import { IAddToCartSection } from './types';
import * as S from './styles';

export const AddToCartSection: React.FC<IAddToCartSection> = ({
  items,
  attributes,
  formAttributes,
  productAttributes,
  onAddToCart,
  isSampleType,
  setActiveImage,
  product,
  productId,
  productName,
  productPricing,
  variantId,
  variantPricing,
  variantStock,
}) => {
  const intl = useIntl();

  const [quantity, setQuantity] = useState<number>(1);

  const availableQuantity = getAvailableQuantity(
    items,
    variantId,
    variantStock
  );
  const isOutOfStock = !!variantId && variantStock === 0;
  const { isAvailableForPurchase, availableForPurchase } = product;
  const noPurchaseAvailable = !isAvailableForPurchase && !availableForPurchase;
  const purchaseAvailableDate =
    !isAvailableForPurchase &&
    availableForPurchase &&
    Date.parse(availableForPurchase);
  const isNoItemsAvailable =
    !variantId || (!!variantId && !isOutOfStock && !availableQuantity);

  const isLowStock =
    !!variantId &&
    !isOutOfStock &&
    !isNoItemsAvailable &&
    availableQuantity < LOW_STOCK_QUANTITY;

  const disableButton = !isSampleType
    ? !canAddToCart(
        items,
        !!isAvailableForPurchase,
        variantId,
        variantStock,
        quantity
      )
    : !canAddSampleToCart(productAttributes);

  const renderErrorMessage = (message: string, testingContextId: string) => (
    <S.ErrorMessage
      data-test="stockErrorMessage"
      data-testId={testingContextId}
    >
      {message}
    </S.ErrorMessage>
  );

  const renderSampleMessage = (productAttributes) => {
    const selectedNumber =
      productAttributes[0]?.values.filter(Boolean).length || 0;

    const message = selectedNumber
      ? intl.formatMessage(commonMessages.itemsSelected, {
          selectedNumber: selectedNumber || '',
        })
      : intl.formatMessage(commonMessages.itemsMinMax, {
          maxItems: MAX_SAMPLE_ITEMS || '',
          minItems: MIN_SAMPLE_ITEMS || '',
        });

    return (
      <S.ErrorMessage data-test="stockErrorMessage" data-testId="sampleCount">
        {message}
      </S.ErrorMessage>
    );
  };

  return (
    <S.AddToCartSection>
      <S.ContentContainer>
        <S.Header>
          <S.ProductNameHeader>{productName}</S.ProductNameHeader>
          {isOutOfStock ? (
            renderErrorMessage(
              intl.formatMessage(commonMessages.outOfStock),
              'outOfStock'
            )
          ) : (
            <S.ProductPricing>
              {getProductPrice(productPricing, variantPricing)}
            </S.ProductPricing>
          )}
        </S.Header>
        {noPurchaseAvailable ? (
          renderErrorMessage(
            intl.formatMessage(commonMessages.noPurchaseAvailable),
            'notAvailable'
          )
        ) : (
          <S.EstimatedShipping>
            <EstimatedShipping productType={product.productType} type="long" />
          </S.EstimatedShipping>
        )}
        {purchaseAvailableDate &&
          renderErrorMessage(
            intl.formatMessage(commonMessages.purchaseAvailableOn, {
              date: new Intl.DateTimeFormat('default', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              }).format(purchaseAvailableDate),
              time: new Intl.DateTimeFormat('default', {
                hour: 'numeric',
                minute: 'numeric',
              }).format(purchaseAvailableDate),
            }),
            'timeRestrictedAvailability'
          )}
        {isLowStock &&
          renderErrorMessage(
            intl.formatMessage(commonMessages.lowStock),
            'lowStockWarning'
          )}
        {isNoItemsAvailable &&
          renderErrorMessage(
            intl.formatMessage(commonMessages.noItemsAvailable),
            'noItemsAvailable'
          )}
        {isSampleType && renderSampleMessage(productAttributes)}
        {(isSampleType || product.productType.hasVariants) && (
          <ProductVariantPicker
            attributes={attributes}
            isSampleType={isSampleType}
            setActiveImage={setActiveImage}
            productType={product.productType.slug}
          />
        )}
      </S.ContentContainer>
      <S.ButtonContainer>
        <S.QuantityContainer>
          <QuantityInput
            quantity={quantity}
            maxQuantity={availableQuantity}
            disabled={isOutOfStock || isNoItemsAvailable}
            onQuantityChange={setQuantity}
            hideErrors={!variantId || isOutOfStock || isNoItemsAvailable}
          />
        </S.QuantityContainer>
        <S.ActionContainer>
          <SaveForLaterButton
            product={product}
            formAttributes={formAttributes}
            productAttributes={productAttributes}
            productId={productId}
            productName={productName}
            variantId={variantId}
          />
          <AddToCartButton
            onSubmit={() => onAddToCart(quantity)}
            disabled={disableButton}
            price={getProductPrice(productPricing, variantPricing)}
          />
        </S.ActionContainer>
      </S.ButtonContainer>
    </S.AddToCartSection>
  );
};
